import { clone, isEqual } from "lodash-es";
import type { IFilterField } from "~~/types/filter";

export class Filter {

    public fields: IFilterField[];
    public values: any;

    public static knownSuffixes = {
        'цена': '₽',
        'длина': 'мм',
        'ширина': 'мм',
        'высота': 'мм',
        'глубина': 'мм',
    }

    public constructor(fields: IFilterField[]) {
        this.values = reactive(this.createValues(fields));
        this.fields = this.createFilter(fields);
    }

    public restore(values: any) {
        Object.assign(this.values, values);
    }

    public toJSON() {
        return {
            fields: this.fields,
            values: this.values
        }
    }

    public restoreValues(values: {[key: string]: string[] | number[] | string}) {
        this.fields.forEach((field) => {
            if (typeof values[field.code] === 'undefined') {
                return;
            }
            const newValue = values[field.code];

            if (field.type === 'range' && Array.isArray(newValue) && newValue.length === 2) {
                this.values[field.key] = clone(newValue);
            } else if (field.type === 'bool') {
                this.values[field.key] = !!newValue;
            } else if (field.type === 'material') {
                this.values[field.key] = clone(newValue);
            } else if (field.type === 'compound' && Array.isArray(newValue)) {
                // @ts-ignore
                this.values[field.key] = newValue.includes(field.value);
            } else if (field.type === 'section' && typeof newValue === 'string') {
                this.values[field.key] = newValue;
            }
        })
    }

    public patch(fields: IFilterField[]) {
        // @ts-ignore
        const usedFields = new Set(this.activeValues.map((el) => el.key ?? el.code));
        const incomingFields = new Map<string, IFilterField>(fields.map((el) => [el.key ?? el.code, el]));

        this.fields.forEach((field) => {

            const key = field.key ?? field.code;

            const isUsed = usedFields.has(key);
    
            if (!incomingFields.has(key) && !isUsed) {
                field.active = false;
                return;
            } else {
                field.active = true;
            }

            const newField = incomingFields.get(key);

            if (newField?.type !== field.type) {
                return;
            }

            if (field.type === 'range' && newField.type === 'range') {
    
                if (!isUsed || field.bounds[0] > newField.bounds[0]) {
                    field.bounds[0] = newField.bounds[0];
                    if (!isUsed) {
                        // @ts-ignore
                        this.values[field.code][0] = field.bounds[0];
                    }
                }
                if (!isUsed || field.bounds[1] < newField.bounds[1]) {
                    field.bounds[1] = newField.bounds[1];
                    if (!isUsed) {
                        // @ts-ignore
                        this.values[field.code][1] = field.bounds[1];
                    }
                }
    
                if (!isUsed && field.bounds[0] === field.bounds[1]) {
                    field.active = false;
                }
    
            } else if (field.type === 'material' && newField.type === 'material') {
                // добиваем недостающие
                newField.set.forEach((el) => {
                    if (!field.set.some((f) => f.value === el.value)) {
                        field.set.push(el);
                    }
                });
    
                // убираем то, чего больше нет
                if (!isUsed) {
                    field.set = newField.set;
    
                    if (field.set.length < 2) {
                        field.active = false;
                    }
                }
            } else if (field.type === 'section' && newField.type == 'section') {
                // убираем то, чего больше нет
                if (!isUsed) {
                    field.set = newField.set;
    
                    if (field.set.length < 2) {
                        field.active = false;
                    }
                } else {
                    // добиваем недостающие
                    newField.set.forEach((el) => {
                        if (!field.set.some((f) => f.code === el.code)) {
                            field.set.push(el);
                        }
                    });
                }
            }
        });
    }

    public resetField(code: string) {

        const field = this.fields.find((el) => el.code === code);

        if (!field) {
            return;
        }

        if (field.type === 'range' && field.bounds && field.bounds[0] !== field.bounds[1]) {
            this.values[field.key] = clone(field.bounds);
        } else if (field.type === 'material' && field.set?.length) {
            this.values[field.key].clear();
        } else if (field.type === 'bool' || field.type === 'compound') {
            this.values[field.key] = false;
        } else if (field.type === 'terms') {
            this.values[field.key] = {amount: 0, terms: 0};
        } else if (field.type === 'section') {
            this.values[field.key] = null;
        }
    }

    public reset() {
        this.fields.forEach((field) => {
            if (field.type === 'range' && field.bounds && field.bounds[0] !== field.bounds[1]) {
                this.values[field.key] = clone(field.bounds);
            } else if (field.type === 'material' && field.set?.length) {
                this.values[field.key] = new Set();
            } else if (field.type === 'bool' || field.type === 'compound') {
                this.values[field.key] = false;
            } else if (field.type === 'terms') {
                this.values[field.key] = {amount: 0, terms: 0};
            } else if (field.type === 'section') {
                this.values[field.key] = null;
            }
        });
    }

    public readonly activeValues = computed(() => {
        const result: any[] = [];

        const compoundFilters = new Map();

        this.fields.forEach((field) => {

            const value = this.values[field.key];
            if (
                (field.type === 'range' && !isEqual(field.bounds, value)) ||
                (field.type === 'bool' && value) || 
                (field.type === 'terms' && value.amount) ||
                (field.type === 'section' && value)
            ) {
                result.push({
                    type: field.type,
                    code: field.code,
                    values: clone(unref(value))
                });
            } else if (field.type === 'material' && value.size) {
                result.push({
                    type: field.type,
                    code: field.code,
                    values: [...(unref(value) as Set<any>).values()]
                });
            }
             else if(field.type === 'compound' && value) {
                const value = compoundFilters.get(field.code) ?? [];
                value.push(unref(field.value));
                compoundFilters.set(field.code, value);
            }
        })

        compoundFilters.forEach((value, key) => {
            result.push({
                type: 'bool',
                code: key,
                values: value
            });
        })

        return result;
    });

    public readonly activeFieldCodes = computed(() => {
        const result = new Set<string>();

        this.fields.forEach((field) => {

            const value = this.values[field.key];
            if (
                (field.type === 'range' && !isEqual(field.bounds, value)) ||
                (field.type === 'bool' && value) || 
                (field.type === 'terms' && value.amount) ||
                (field.type === 'section' && value) ||
                (field.type === 'material' && value.size) ||
                (field.type === 'compound' && value)
            ) {
                result.add(field.code);
            }
        });

        return result;
    });

    public readonly isActive = computed(() => !!this.activeValues.value.length)

    private createFilter(fields: IFilterField[]) {
        const filter = fields.filter((field) => typeof this.values[field.key] !== 'undefined');

        filter.forEach(el => {
            el.active = true;
            if (el.type === 'range') {
                const name: string = el.name.toLowerCase();
                for (const key in Filter.knownSuffixes)  {
                    if (name.includes(key)) {
                        // @ts-ignore
                        el.suffix = Filter.knownSuffixes[key];
                        break;
                    }
                }
            }
        });

        return filter;
    }

    private createValues(fields: IFilterField[]) {
        const values: any = {};

        fields.forEach((field) => {
            if (field.type === 'range' && field.bounds && field.bounds[0] !== field.bounds[1]) {
                values[field.key] = clone(field.bounds);
            } else if (field.type === 'material' && field.set?.length) {
                values[field.key] = new Set();
            } else if (field.type === 'bool' || field.type === 'compound') {
                values[field.key] = false;
            } else if (field.type === 'terms') {
                values[field.key] = {amount: 0, terms: 0};
            } else if (field.type === 'section') {
                values[field.key] = null;
            }
        });

        return values;
    }
}
